import '../text-and-lang/as-text.ts';
import styleText from './numbox.css?raw';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence } from './utils';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';

/**
 * # NumberValueBox Component
 *
 * A versatile numeric input component that provides multiple ways to interact with numeric values.
 * Features an input field, optional increment/decrement controls, and an optional slider for visual manipulation.
 *
 * ## Features
 * - Direct numeric input with validation
 * - Increment/decrement buttons with customizable step values
 * - Optional slider for visual value selection
 * - Unit display support
 * - Customizable decimal precision
 * - Min/max value constraints
 * - Keyboard navigation (arrow keys, page up/down)
 * - Mouse wheel support for value adjustment
 * - Event throttling with cooloff periods
 * - Localized label support
 * - Paramate integration support
 * - Shadow DOM encapsulation option
 * - Type attribute ('float' | 'int') with automatic coercion
 *
 * ## Component Hierarchy
 * ```
 * NumberValueBox (as-numbox)
 * ├── label - Optional localized label
 * ├── wrapper
 * │   ├── container - Main input container
 * │   │   ├── input[type="number"] - The numeric input field
 * │   │   └── controls - Increment/decrement buttons
 * │   │       ├── increment (▲)
 * │   │       └── decrement (▼)
 * │   └── unit - Unit display span
 * └── slider-container - Optional slider input
 *     └── input[type="range"] - Range slider
 * ```
 *
 * ## Attributes
 * - `value`: The numeric value (default: 0)
 * - `type`: Numeric type - 'float' (default) or 'int' (rounds values via Math.round)
 * - `unit`: Unit text to display after the value
 * - `decimals`: Number of decimal places to display (default: 2)
 * - `step`: Increment/decrement step size (default: 1)
 * - `min`: Minimum allowed value (default: -Infinity). Author-owned.
 * - `max`: Maximum allowed value (default: Infinity). Author-owned.
 * - `auto-min`: Param-driven minimum, written by Paramate sync. Combined with `min` by
 *   taking the most restrictive (highest) lower bound.
 * - `auto-max`: Param-driven maximum, written by Paramate sync. Combined with `max` by
 *   taking the most restrictive (lowest) upper bound.
 * - `visible-digits`: Total character width of the input field (including decimal point and dp digits).
 *   E.g. `visible-digits="8"` with `decimals="2"` shows `NNNNN.dd` (5 integer + dot + 2 dp).
 *   Does not alter the stored value — purely controls field width. When the value is wider than
 *   the visible area, hovering over the input plays a marquee scroll to reveal the full number.
 * - `show-slider`: When present, displays the slider control
 * - `slider-width`: Explicit width for the slider (e.g. "100px", "50%"). If not set, slider fits numbox.
 * - `label`: Label text to display above the input
 * - `label-dict-key`: Localization key for the label
 * - `label-default-text`: Default label text when localization key is not found
 * - `align`: Text alignment in input field ('left' or 'right', default: 'right')
 * - `trcad-param': Parameter name for Paramate integration
 * - `cooloff`: Cooloff period in milliseconds for event throttling (default: 0)
 * - `shadow-dom`: When present, uses Shadow DOM for encapsulation
 * - `show-arrow-buttons`: When present, shows the increment/decrement arrow buttons (default: hidden)
 *
 * ## CSS Custom Properties
 * - `--as-font-family-base`: Font family for the component
 * - `--as-primary`: Primary color for slider thumb (default: #007acc)
 * - `--as-text-secondary`: Secondary text color for labels (default: #666)
 * - `--numbox-label-gap`: Gap between label and inputs (default: 4px)
 * - `--numbox-padding`: Component padding (default: 4px)
 * - `--numbox-padding-right`: Right padding (default: 1rem)
 * - `--numbox-border-radius`: Border radius (default: 6px)
 * - `--numbox-container-border-radius`: Input container border radius (default: 1rem)
 * - `--numbox-container-border-color`: Container border color (default: #ccc)
 * - `--numbox-container-border-width`: Container border width (default: 2px)
 * - `--numbox-container-bg`: Container background color (default: white)
 * - `--numbox-container-padding`: Container padding (default: 4px 8px)
 * - `--numbox-focus-color`: Focus outline color (default: #007acc)
 * - `--numbox-focus-width`: Focus outline width (default: 2px)
 * - `--numbox-text-color`: Text color (default: inherit)
 * - `--numbox-unit-color`: Unit text color (default: #666)
 * - `--numbox-control-btn-size`: Control button size (default: 16px x 12px)
 * - `--numbox-control-btn-bg`: Control button background (default: #f5f5f5)
 * - `--numbox-control-btn-border`: Control button border (default: 1px solid #ccc)
 * - `--numbox-control-btn-hover-bg`: Control button hover background (default: #e5e5e5)
 * - `--numbox-control-btn-active-bg`: Control button active background (default: #ddd)
 * - `--numbox-control-btn-radius`: Control button border radius (default: 2px)
 * - `--numbox-slider-height`: Slider height (default: 4px)
 * - `--numbox-slider-bg`: Slider track background (default: #ddd)
 * - `--numbox-slider-thumb-size`: Slider thumb size (default: 12px)
 * - `--numbox-slider-thumb-color`: Slider thumb color (default: var(--as-primary, #007acc))
 * - `--numbox-label-font-size`: Label font size (default: 0.85em)
 * - `--numbox-label-color`: Label color (default: var(--as-text-secondary, #666))
 * - `--numbox-label-height`: Label height (default: 1.2em)
 * - `--numbox-input-width`: Fixed width for the input field (overrides auto-sizing)
 * - `--numbox-slider-width`: Fixed width for the slider (overrides auto-sizing)
 *
 * ## Events
 * ### Emitted Events
 * - `as-input-update`: Dispatched during value changes (with throttling if cooloff > 0)
 *   - Detail: `{ type: 'number', value: number, oldValue: number, formattedValue: string, phase: 'input' }`
 * - `as-input-change`: Dispatched when value changes are finalized
 *   - Detail: `{ type: 'number', value: number, oldValue: number, formattedValue: string, phase: 'change' }`
 * - Legacy aliases `as-numbox-input` / `as-numbox-change` are still emitted for backward compatibility
 *
 * ## Usage Examples
 *
 * ### Basic Usage
 * ```html
 * <as-numbox value="10" unit="px" decimals="0"></as-numbox>
 * ```
 *
 * ### With Slider and Range
 * ```html
 * <as-numbox
 *   value="50"
 *   min="0"
 *   max="100"
 *   step="5"
 *   show-slider>
 * </as-numbox>
 * ```
 *
 * ### With Localized Label
 * ```html
 * <as-numbox
 *   value="25.5"
 *   unit="%"
 *   label-dict-key="opacity"
 *   label-default-text="Opacity">
 * </as-numbox>
 * ```
 *
 * ### With Event Throttling
 * ```html
 * <as-numbox
 *   value="0"
 *   cooloff="300">
 * </as-numbox>
 * ```
 *
 * ### Integer Type
 * ```html
 * <as-numbox
 *   value="5"
 *   type="int"
 *   min="0"
 *   max="100">
 * </as-numbox>
 * ```
 *
 * ### Handling Events
 * ```javascript
 * const numbox = document.querySelector('as-numbox');
 *
 * numbox.addEventListener('as-numbox-input', (e) => {
 *   console.log('Value changing:', e.detail.value);
 * });
 *
 * numbox.addEventListener('as-numbox-change', (e) => {
 *   console.log('Final value:', e.detail.value);
 *   // Save or process the final value
 * });
 * ```
 *
 * ## Interaction Methods
 * - **Keyboard**: Arrow up/down (+-1 step), Page up/down (+-10 steps)
 * - **Mouse**: Click increment/decrement buttons, wheel scroll over input
 * - **Touch**: Direct input editing, slider manipulation
 * - **Programmatic**: Set `value` property or `value` attribute
 *
 * ## Implementation Notes
 * - Automatically clamps values to min/max range
 * - Supports both Shadow DOM and light DOM rendering
 * - Integrates with Paramate library for parameter management
 * - Input field auto-resizes based on content width
 * - Slider width matches input container width when no unit is present
 * - Event throttling prevents excessive event firing during rapid changes
 * - Supports external value changes through property setters
 * - Number input arrows are hidden while maintaining numeric input validation
 *
 * ## Live demos / API reference
 * See `appSrc/tutorial/src/topics/numbox.ts` (and the "Paramate Integration" subtopic in
 * `appSrc/tutorial/src/topics/input-base.ts` for `auto-min`/`auto-max` bounds).
 */

type NumberValueEventDetailBase = {
  type: 'number';
  value: number;
  oldValue: number;
  formattedValue: string;
  dontFocus?: boolean;
};

type NumberValueInputDetail = NumberValueEventDetailBase & { phase: 'input' };
type NumberValueChangeDetail = NumberValueEventDetailBase & { phase: 'change' };

interface NumberValueBoxChangeEvent extends CustomEvent<NumberValueChangeDetail> {
  detail: NumberValueChangeDetail;
}

interface NumberValueBoxInputEvent extends CustomEvent<NumberValueInputDetail> {
  detail: NumberValueInputDetail;
}

// Template function for rendering the label content
const renderLabelContent = (text: string | null, dictKey: string | null, defaultText: string | null) => {
  if (dictKey) {
    return `<as-text dict-key="${dictKey}"${defaultText ? ` default-text="${defaultText}"` : ''}>${text ?? defaultText ?? ''}</as-text>`;
  } else if (text || defaultText) {
    return `<as-text>${text || defaultText || ''}</as-text>`;
  }
  return '';
};

/**
 * NumberValueBox custom element implementation
 * Provides a comprehensive numeric input component with multiple interaction methods
 */
class NumberValueBox extends AsInputBase<'float'> {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private static lightStyleAppended = false;
  private _unit: string = '';
  private _decimals: number = 2;
  private _step: number = 0.01;
  // Effective bounds (used by clamp/slider/width). Derived from author + auto sources.
  private _min: number = -Infinity;
  private _max: number = Infinity;
  // Author-owned bounds (from `min`/`max` attributes or property setters). Take precedence.
  private _minAttr: number | null = null;
  private _maxAttr: number | null = null;
  // Param-owned bounds (from `auto-min`/`auto-max` attributes, written by Paramate sync).
  private _autoMin: number | null = null;
  private _autoMax: number | null = null;
  private _showSlider: boolean = false;
  private _label: string = '';
  private _align: 'left' | 'right' = 'right';
  private _useShadowDOM: boolean = false;
  private _showArrowButtons: boolean = false;
  private _helpTooltipDefaultText: string = '';
  private _helpTooltipDictKey: string = '';
  private _isProcessingInitialAttributes = false;
  private _numericType: 'float' | 'int' = 'float';
  private _sliderUpdating = false;
  private _visibleDigits: number | null = null;
  private _marqueeRaf: number | null = null;
  // Decimal separator — will be linked to browser/app language later
  _separator: '.' | ',' = '.';

  constructor() {
    super();
    this._value = 0;

    this._useShadowDOM = this.hasAttribute('shadow-dom');

    if (this._useShadowDOM) {
      const shadow = this.attachShadow({ mode: 'open', delegatesFocus: true });
      shadow.adoptedStyleSheets = [
        ...shadow.adoptedStyleSheets,
        NumberValueBox.sheet,
      ];
    } else if (!NumberValueBox.lightStyleAppended) {
      const styleEl = document.createElement('style');
      styleEl.textContent = styleText;
      styleEl.setAttribute('data-component', 'as-numbox');
      document.head.appendChild(styleEl);
      NumberValueBox.lightStyleAppended = true;
    }
  }

  static get observedAttributes(): string[] {
    return ['value', 'unit', 'decimals', 'step', 'min', 'max', 'auto-min', 'auto-max', 'show-slider', 'label', 'label-dict-key', 'label-default-text', 'align', 'shadow-dom', 'show-arrow-buttons', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'type', 'visible-digits', 'slider-width'];
  }

  private parseInitialAttributes(): void {
    // Parse all observed attributes that might be present initially
    const observedAttrs = NumberValueBox.observedAttributes;
    const handled = new Set<string>();

    this._isProcessingInitialAttributes = true;
    try {
      for (const attr of observedAttrs) {
        const normalizedAttr = attr.toLowerCase();
        if (handled.has(normalizedAttr)) continue;

        const actualAttrName = this.hasAttribute(attr)
          ? attr
          : normalizedAttr;

        if (this.hasAttribute(actualAttrName)) {
          const value = this.getAttribute(actualAttrName);
          this.attributeChangedCallback(actualAttrName, null, value);
          handled.add(normalizedAttr);
        }
      }
    } finally {
      this._isProcessingInitialAttributes = false;
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value': {
        const parsed = newValue === null || newValue === '' ? 0 : parseFloat(newValue);
        let numericValue = Number.isFinite(parsed) ? parsed : 0;
        if (this._numericType === 'int') numericValue = Math.round(numericValue);

        if (this._isProcessingInitialAttributes) {
          this._value = numericValue;
          this.updateDisplay();
        } else {
          this.setValue(numericValue, { silenceInputEv: true });
        }
        return;
      }
      case 'unit':
        this._unit = newValue ?? '';
        break;
      case 'decimals':
        this._decimals = parseInt(newValue ?? '2') ?? 2;
        break;
      case 'step':
        this._step = parseFloat(newValue ?? '1') ?? 1;
        break;
      case 'min':
        this._minAttr = NumberValueBox.parseBound(newValue);
        this.recomputeBounds();
        break;
      case 'max':
        this._maxAttr = NumberValueBox.parseBound(newValue);
        this.recomputeBounds();
        break;
      case 'auto-min':
        this._autoMin = NumberValueBox.parseBound(newValue);
        this.recomputeBounds();
        break;
      case 'auto-max':
        this._autoMax = NumberValueBox.parseBound(newValue);
        this.recomputeBounds();
        break;
      case 'show-slider':
        this._showSlider = newValue !== null;
        break;
      case 'slider-width':
        if (newValue) {
          this.style.setProperty('--numbox-slider-width', newValue);
        } else {
          this.style.removeProperty('--numbox-slider-width');
        }
        break;
      case 'label':
        this._label = newValue ?? '';
        break;
      case 'align':
        this._align = (newValue as 'left' | 'right') ?? 'right';
        break;
      case 'shadow-dom':
        // This attribute is only checked during construction
        break;
      case 'show-arrow-buttons':
        this._showArrowButtons = newValue !== null;
        break;
      case 'help-tooltip-default-text':
        this._helpTooltipDefaultText = newValue ?? '';
        break;
      case 'help-tooltip-dict-key':
        this._helpTooltipDictKey = newValue ?? '';
        break;
      case 'type':
        this._numericType = newValue === 'int' ? 'int' : 'float';
        this.trCadType = this._numericType as any;
        // Re-coerce current value if switching to int
        if (this._numericType === 'int') {
          this._value = Math.round(this._value);
          this.updateDisplay();
        }
        break;
      case 'visible-digits':
        this._visibleDigits = newValue === null ? null : parseInt(newValue);
        break;
    }

    this.updateDisplay();
  }

  // ============================================================================
  // Abstract method implementations (required by AsInputBase)
  // ============================================================================

  protected _getValue(): number {
    return this._value;
  }

  protected _applyValue(val: number, options: InternalSetValueOptions): void {
    const oldValue = this._value;
    let newValue = this.clampValue(val);
    if (this._numericType === 'int') {
      newValue = Math.round(newValue);
    }

    this._value = newValue;
    this.updateDisplay();

    if (options.emitInput || options.emitChange) {
      const eventDetail: NumberValueEventDetailBase = {
        type: 'number',
        value: newValue,
        oldValue,
        formattedValue: this.formatValue(newValue),
        dontFocus: (options as any)?.dontFocus,
      };

      if (options.emitInput) {
        this._throttle('input', () => this.emitInputEvent(eventDetail));
      }
      if (options.emitChange) {
        this._throttle('change', () => this.emitChangeEvent(eventDetail));
      }
    }
  }

  protected _convertFromParamate(val: any): number {
    const numericValue = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(numericValue)) return this._value;
    let result = this.clampValue(numericValue);
    if (this._numericType === 'int') {
      result = Math.round(result);
    }
    return result;
  }

  public override resetToDefault(): void {
    const attrValue = this._initialValueAttr;
    const parsed = attrValue === null || attrValue === '' ? 0 : parseFloat(attrValue);
    const numericValue = Number.isFinite(parsed) ? parsed : 0;
    this.setValue(numericValue, { silenceEvents: true });
  }

  protected _getInputElement(): HTMLInputElement | null {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    return root.querySelector('.value') as HTMLInputElement | null;
  }

  protected override _applyReadonlyState(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const valueInput = root.querySelector('.value') as HTMLInputElement | null;
    const slider = root.querySelector('.slider') as HTMLInputElement | null;
    const incrementBtn = root.querySelector('.increment') as HTMLElement | null;
    const decrementBtn = root.querySelector('.decrement') as HTMLElement | null;
    const isReadonly = this.readonly;

    if (valueInput) {
      valueInput.readOnly = isReadonly;
    }
    if (slider) {
      slider.disabled = isReadonly;
    }
    if (incrementBtn) {
      incrementBtn.style.pointerEvents = isReadonly ? 'none' : '';
    }
    if (decrementBtn) {
      decrementBtn.style.pointerEvents = isReadonly ? 'none' : '';
    }
  }

  // ============================================================================
  // Rendering
  // ============================================================================

  private render(): void {
    const placement = this.getAttribute("label-placement") ?? "top";

    const html = `
      <div class="as-numbox placement-${placement}${this._showArrowButtons ? ' show-arrow-buttons' : ''}">
        <div class="numbox-label-group"></div>
        <div class="inputs">
          <div class="wrapper">
            <div class="inputs-container">
              <div class="container">
                <input type="number" class="value align-${this._align}">
                <div class="controls">
                  <div class="control-btn increment" tabindex="-1">▲</div>
                  <div class="control-btn decrement" tabindex="-1">▼</div>
                </div>
                <span class="unit"></span>
              </div>
              <div class="slider-container">
                <input type="range" class="slider" tabindex="0">
              </div>
            </div>
          </div>
        </div>
        <div class="light-dom-children"></div>
      </div>
    `;

    if (this._useShadowDOM) {
      this.shadowRoot!.innerHTML = html;
    } else {
      // Preserve light DOM children (like as-hovercard) before replacing innerHTML
      const existingChildren = Array.from(this.children);
      this.innerHTML = html;

      // Move preserved children to the hidden container
      const lightDomContainer = this.querySelector('.light-dom-children');
      if (lightDomContainer) {
        existingChildren.forEach(child => {
          lightDomContainer.appendChild(child);
        });
      }
    }
  }

  private setupEventListeners(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const container = root.querySelector('.container') as HTMLElement;
    const valueInput = root.querySelector('.value') as HTMLInputElement;
    const incrementBtn = root.querySelector('.increment') as HTMLElement;
    const decrementBtn = root.querySelector('.decrement') as HTMLElement;
    const slider = root.querySelector('.slider') as HTMLInputElement;

    // Keyboard events
    // Removed: Keyboard events now handled on valueInput for better tabbing/editing

    // Scroll events
    this.hasAttribute("listen-wheel")&&container.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        this.increment();
      } else {
        this.decrement();
      }
    });

    // Button events
    incrementBtn.addEventListener('click', () => this.increment());
    decrementBtn.addEventListener('click', () => this.decrement());

    // Focus the input on any click within the component
    this.addEventListener('click', (e: MouseEvent) => {
      // Don't steal focus if the user clicked directly on the input or slider
      if (e.target !== valueInput && e.target !== slider) {
        valueInput.focus();
      }
    });

    // Select all on double-click
    valueInput.addEventListener('dblclick', () => {
      valueInput.select();
    });

    // Track whether the user has actually edited the text input since it was last
    // programmatically updated. Used in blur to avoid spurious change events when
    // the component itself updated the display (e.g. during slider interaction).
    let valueInputUserEdited = false;

    valueInput.addEventListener('input', () => {
      valueInputUserEdited = true;
      const parsed = this.parseInputText(valueInput.value);
      if (!isNaN(parsed)) {
        const eventDetail: NumberValueEventDetailBase = {
          type: 'number',
          value: parsed,
          oldValue: this._value,
          formattedValue: this.formatValue(parsed),
        };
        this._throttle('input', () => this.emitInputEvent(eventDetail));
      }
    });

    valueInput.addEventListener('blur', () => {
      if (valueInputUserEdited) {
        // Only re-parse and commit if the user actually typed something.
        // Without this guard, updateDisplay() sets the input to the formatted
        // (decimals-truncated) string, blur re-parses it, and precision is lost.
        const newValue = this.parseInputText(valueInput.value);
        if (!isNaN(newValue)) {
          this.setValue(newValue, { silenceInputEv: true });
        }
      }
      valueInputUserEdited = false;
      this.updateDisplay();
    });

    valueInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const newValue = this.parseInputText(valueInput.value);
        if (!isNaN(newValue)) {
          this.setValue(newValue);
        }
        valueInput.blur();
      } else if (e.key === 'Escape') {
        this.updateDisplay();
      }
      if(this.hasAttribute("listen-arrow")){
        // Handle increment/decrement with arrow keys and page up/down
        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            this.increment();
            break;
          case 'ArrowDown':
            e.preventDefault();
            this.decrement();
            break;
          case 'PageUp':
            e.preventDefault();
            this.increment(10);
            break;
          case 'PageDown':
            e.preventDefault();
            this.decrement(10);
            break;
        }
      } else {
        // Prevent default arrow key behavior when listen-arrow is not set
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    });

    // Slider events — dragStartValue lives in this closure, not on the instance
    let dragStartValue: number | null = null;

    // Capture start value at pointerdown, which fires BEFORE the text input's blur
    // event (and any setValue call it might trigger). The input handler below is a
    // fallback for keyboard-driven slider interaction (no preceding pointerdown).
    slider.addEventListener('pointerdown', () => {
      if (!this.readonly) dragStartValue = this._value;
    });

    slider.addEventListener('input', (e: Event) => {
      if (this.readonly || this._sliderUpdating) return;
      if (dragStartValue === null) dragStartValue = this._value; // keyboard fallback
      const target = e.target as HTMLInputElement;
      const newValue = parseFloat(target.value);
      this.setValue(newValue, { silenceChangeEv: true });
    });

    slider.addEventListener('change', () => {
      if (this.readonly || this._sliderUpdating) return;
      const oldValue = dragStartValue ?? this._value;
      dragStartValue = null;
      if (this._value === oldValue) return;
      const eventDetail: NumberValueEventDetailBase = {
        type: 'number',
        value: this._value,
        oldValue,
        formattedValue: this.formatValue(this._value),
      };
      this.emitChangeEvent(eventDetail);
    });

    // Marquee: when visible-digits clips the text, scroll through it on hover
    const startMarquee = () => {
      if (this._visibleDigits === null) return;
      const maxScroll = valueInput.scrollWidth - valueInput.clientWidth;
      if (maxScroll <= 1) return;

      const period = 2000; // ms per left-to-right pass
      let startTime: number | null = null;

      const tick = (ts: number) => {
        if (startTime === null) startTime = ts;
        const t = ((ts - startTime) % (period * 2)) / period; // 0..2 ping-pong
        // For right alignment, start from maxScroll (right), go to 0 (left), and back
        valueInput.scrollLeft = (t <= 1 ? 1 - t : t - 1) * maxScroll;
        this._marqueeRaf = requestAnimationFrame(tick);
      };
      this._marqueeRaf = requestAnimationFrame(tick);
    };

    const stopMarquee = () => {
      if (this._marqueeRaf !== null) {
        cancelAnimationFrame(this._marqueeRaf);
        this._marqueeRaf = null;
      }
      valueInput.scrollLeft = this._align === 'right' ? valueInput.scrollWidth : 0;
    };

    container.addEventListener('mouseenter', startMarquee);
    container.addEventListener('mouseleave', stopMarquee);
  }

  private formatValue(value: number): string {
    const s = value.toFixed(this._decimals);
    return this._separator === ',' ? s.replace('.', ',') : s;
  }

  private parseInputText(text: string): number {
    return parseFloat(this._separator === ',' ? text.replace(',', '.') : text);
  }

  private updateDisplay(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const valueInput = root.querySelector('.value') as HTMLInputElement;
    if(!valueInput)return
    const unitSpan = root.querySelector('.unit') as HTMLSpanElement;
    const slider = root.querySelector('.slider') as HTMLInputElement;
    const sliderContainer = root.querySelector('.slider-container') as HTMLElement;
    const labelDiv = root.querySelector('.numbox-label-group') as HTMLElement;

    const formattedValue = this.formatValue(this._value);
    valueInput.value = formattedValue;
    unitSpan.textContent = this._unit;

    // Toggle .empty class if unit is empty
    if (!this._unit || this._unit.trim() === "") {
      unitSpan.classList.add("empty");
    } else {
      unitSpan.classList.remove("empty");
    }

    // Set width based on content (drives wrapper size)
    this.updateInputWidth(valueInput, formattedValue);

    // Update slider
    this.updateSlider(slider, sliderContainer);

    // Update label
    this.updateLabel(labelDiv);

    // Update Paramate data attribute
    // this.updateParamName(valueInput);
  }

  private updateLabel(labelDiv: HTMLElement): void {
    const labelText = this._label;
    const labelDictKey = this.getAttribute('label-dict-key');
    const labelDefaultText = this.getAttribute('label-default-text');
    // Use help-placement attribute for hovercard/tooltip placement, default to "right"
    const placement = this.getAttribute('help-placement') ?? 'right';

    // Check for a direct child <as-hovercard> in the light DOM
    let hovercard: HTMLElement | null = null;
    // Only check in light DOM, not shadow
    if (!this._useShadowDOM) {
      // First, check if it's already in the label div (from a previous render)
      const existingWrapper = labelDiv.querySelector('as-hovercard-wrapper');
      if (existingWrapper) {
        hovercard = existingWrapper.querySelector('as-hovercard') as HTMLElement | null;
      }

      // If not found, look in the preserved light DOM children container
      if (!hovercard) {
        const lightDomContainer = this.querySelector('.light-dom-children');
        if (lightDomContainer) {
          hovercard = Array.from(lightDomContainer.children).find(
            (el) => el.tagName && el.tagName.toLowerCase() === 'as-hovercard'
          ) as HTMLElement | null;
        }
      }
    } else if (this.shadowRoot) {
      // If using shadow DOM, check the shadow host's assigned nodes
      hovercard = Array.from(this.childNodes).find(
        (el) =>
          el.nodeType === Node.ELEMENT_NODE &&
          (el as Element).tagName.toLowerCase() === 'as-hovercard'
      ) as HTMLElement | null;
    }

    // Show label and/or help icon if there's label text OR a hovercard/tooltip
    const hasLabel = !!(labelText || labelDictKey || labelDefaultText);
    const hasHelp = !!(hovercard || this._helpTooltipDictKey || this._helpTooltipDefaultText);

    if (hasLabel || hasHelp || this.isParamMissing) {
      const labelContent = hasLabel ? renderLabelContent(labelText, labelDictKey, labelDefaultText) : '';
      const warningContent = `<as-param-warning trcad-param="${this._paramName}" is-missing="${this.isParamMissing}"></as-param-warning>`;

      if (hovercard) {
        // Remove the hovercard from its current parent to avoid duplication
        if (hovercard.parentElement) {
          hovercard.parentElement.removeChild(hovercard);
        }
        // Render the help icon inside a hovercard wrapper, and insert the hovercard
        labelDiv.innerHTML = `
          ${labelContent}
          <as-hovercard-wrapper placement="${placement}" show-on-hover>
            <span class="help-icon">?</span>
          </as-hovercard-wrapper>
          ${warningContent}
        `;
        // Move the hovercard element into the wrapper
        const wrapper = labelDiv.querySelector('as-hovercard-wrapper');
        if (wrapper) {
          // The close button is opt-in via show-close; strip it so the
          // hover-help card never renders one.
          hovercard.removeAttribute('show-close');
          wrapper.appendChild(hovercard);
        }
      } else if (this._helpTooltipDictKey || this._helpTooltipDefaultText) {
        const helpTooltipAttrs = [];
        if (this._helpTooltipDictKey) {
          helpTooltipAttrs.push(`dict-key="${this._helpTooltipDictKey}"`);
        }
        if (this._helpTooltipDefaultText) {
          helpTooltipAttrs.push(`default-text="${this._helpTooltipDefaultText}"`);
        }

        labelDiv.innerHTML = `
          ${labelContent}
          <as-tooltip-wrapper placement="${placement}" show-on-hover ${helpTooltipAttrs.join(' ')}>
            <span class="help-icon">?</span>
          </as-tooltip-wrapper>
          ${warningContent}
        `;
      } else {
        labelDiv.innerHTML = labelContent + warningContent;
      }
    } else {
      labelDiv.innerHTML = '';
    }
  }

  private updateSlider(slider: HTMLInputElement, container: HTMLElement): void {
    if (this._showSlider) {
      container.classList.add('show');

      // Set slider range - use fixed defaults if min/max not explicitly set
      let min = this._min === -Infinity ? 0 : this._min;
      let max = this._max === Infinity ? 100 : this._max;

      slider.min = min.toString();
      slider.max = max.toString();
      slider.step = this._step.toString();
      this._sliderUpdating = true;
      slider.value = this._value.toString();
      this._sliderUpdating = false;
    } else {
      container.classList.remove('show');
    }
  }

  private updateInputWidth(input: HTMLInputElement, text: string): void {
    // Check if width is overridden by CSS custom property
    const computedStyle = getComputedStyle(input);
    const customWidth = computedStyle.getPropertyValue('--numbox-input-width').trim();

    if (customWidth && customWidth !== '') {
      input.style.width = customWidth;
      return;
    }

    // visible-digits is the total visible character width (includes dp digits + decimal point).
    // The value is never altered — this is display width only.
    if (this._visibleDigits !== null && !isNaN(this._visibleDigits)) {
      input.style.width = `${this._visibleDigits}ch`;
      return;
    }

    // Calculate the width needed for the widest possible value within the range
    const texts = [text ?? '0'];
    if (this._min !== -Infinity && isFinite(this._min)) texts.push(this.formatValue(this._min));
    if (this._max !== Infinity && isFinite(this._max)) texts.push(this.formatValue(this._max));

    let maxLen = 0;
    for (const t of texts) {
      let l = t.length;
      // If we have negative numbers in range, reserve space for the minus sign 
      // on strings that don't have it yet, to keep width stable.
      if (this._min < 0 && !t.startsWith('-')) {
        l++;
      }
      if (l > maxLen) maxLen = l;
    }

    input.style.width = `${maxLen}ch`;
  }


  /** Parse a bound attribute value to a finite number, or null if absent/invalid. */
  private static parseBound(raw: string | null): number | null {
    if (raw === null || raw === '') return null;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Recompute the effective min/max from author and param-driven sources.
   * The most restrictive of the two wins: effective min = highest of the author (`min`)
   * and param (`auto-min`) lower bounds; effective max = lowest of the author (`max`) and
   * param (`auto-max`) upper bounds. A missing source is unbounded on its side. This lets
   * authors tighten the range while never allowing a value the param would reject.
   * Re-clamps the current value and refreshes display.
   */
  private recomputeBounds(): void {
    this._min = Math.max(this._minAttr ?? -Infinity, this._autoMin ?? -Infinity);
    this._max = Math.min(this._maxAttr ?? Infinity, this._autoMax ?? Infinity);
    // Re-clamp the current value to the new range (silent — bounds changes aren't user edits)
    this.setValue(this._value, { silenceEvents: true });
    this.updateDisplay();
  }

  private clampValue(value: number): number {
    return Math.max(this._min, Math.min(this._max, value));
  }

  private increment(multiplier: number = 1): void {
    if (this.readonly) return;
    const newValue = this.clampValue(this._value + (this._step * multiplier));
    this.setValue(newValue);
  }

  private decrement(multiplier: number = 1): void {
    if (this.readonly) return;
    const newValue = this.clampValue(this._value - (this._step * multiplier));
    this.setValue(newValue);
  }

  public setValue(value: number, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange, silenceHistory } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;

    const oldValue = this._value;
    let newValue = this.clampValue(value);
    if (this._numericType === 'int') {
      newValue = Math.round(newValue);
    }

    if (oldValue !== newValue) {
      this._value = newValue;
      this.updateDisplay();

      const eventDetail: NumberValueEventDetailBase = {
        type: 'number',
        value: newValue,
        oldValue,
        formattedValue: this.formatValue(newValue),
        dontFocus: opt?.dontFocus,
      };

      if (!silenceInput) {
        this._throttle('input', () => this.emitInputEvent(eventDetail));
      }

      if (!silenceChange) {
        this._throttle('change', () => this.emitChangeEvent(eventDetail));
      }
    }
    this._skipNextHistoryPush = false;
  }

  private emitInputEvent(eventDetail: NumberValueEventDetailBase): void {
    const genericEvent: NumberValueBoxInputEvent = new CustomEvent<NumberValueInputDetail>('as-input-update', {
      detail: {
      ...eventDetail,
      phase: 'input',
    },
      bubbles: true,
      composed: true,
    }) as NumberValueBoxInputEvent;

    this.dispatchEvent(genericEvent);
  }

  private emitChangeEvent(eventDetail: NumberValueEventDetailBase): void {
    const detailWithPhase: NumberValueChangeDetail = {
      ...eventDetail,
      phase: 'change',
    };

    const genericEvent: NumberValueBoxChangeEvent = new CustomEvent<NumberValueChangeDetail>('as-input-change', {
      detail: detailWithPhase,
      bubbles: true,
      composed: true,
    }) as NumberValueBoxChangeEvent;
    this.dispatchEvent(genericEvent);

    // Legacy event for backward compatibility
    const legacyEvent: NumberValueBoxChangeEvent = new CustomEvent<NumberValueChangeDetail>('as-numbox-change', {
      detail: detailWithPhase,
      bubbles: true,
    }) as NumberValueBoxChangeEvent;
    this.dispatchEvent(legacyEvent);

    // Send to Paramate via base class
    this._sendToParamate(eventDetail.value);
  }

  // Getters and setters
  get value(): number {
    return this._value;
  }

  set value(val: number) {
    this.setValue(val);
  }

  get unit(): string {
    return this._unit;
  }

  set unit(val: string) {
    this._unit = val;
    this.updateDisplay();
  }

  get decimals(): number {
    return this._decimals;
  }

  set decimals(val: number) {
    this._decimals = parseInt(val.toString()) ?? 2;
    this.updateDisplay();
  }

  get step(): number {
    return this._step;
  }

  set step(val: number) {
    this._step = parseFloat(val.toString()) ?? 1;
    this.updateDisplay();
  }

  get min(): number {
    return this._min;
  }

  set min(val: number | null) {
    // An explicit JS set is treated as an author-owned bound.
    this._minAttr = val === null ? null : NumberValueBox.parseBound(val.toString());
    this.recomputeBounds();
  }

  get max(): number {
    return this._max;
  }

  set max(val: number | null) {
    // An explicit JS set is treated as an author-owned bound.
    this._maxAttr = val === null ? null : NumberValueBox.parseBound(val.toString());
    this.recomputeBounds();
  }

  get visibleDigits(): number | null {
    return this._visibleDigits;
  }

  set visibleDigits(val: number | null) {
    this._visibleDigits = val === null ? null : parseInt(val.toString());
    this.updateDisplay();
  }

  get showSlider(): boolean {
    return this._showSlider;
  }

  set showSlider(val: boolean) {
    this._showSlider = val;
    this.updateDisplay();
  }

  get label(): string {
    return this._label;
  }

  set label(val: string) {
    this._label = val;
    this.updateDisplay();
  }

  get align(): 'left' | 'right' {
    return this._align;
  }

  set align(val: 'left' | 'right') {
    this._align = val;
    this.updateDisplay();
  }

  get numericType(): 'float' | 'int' {
    return this._numericType;
  }

  set numericType(val: 'float' | 'int') {
    this._numericType = val === 'int' ? 'int' : 'float';
    this.trCadType = this._numericType as any;
    if (this._numericType === 'int') {
      this._value = Math.round(this._value);
      this.updateDisplay();
    }
  }

  connectedCallback(): void {
    this.render();

    // Parse initial attributes after render
    this.parseInitialAttributes();

    this.setupEventListeners();

    // Ensure all attributes are processed before updating display
    requestAnimationFrame(() => {
      this.updateDisplay();

      // Register with Paramate (from base class)
      this._registerParamate();
    });
  }

  disconnectedCallback(): void {
    if (this._marqueeRaf !== null) {
      cancelAnimationFrame(this._marqueeRaf);
      this._marqueeRaf = null;
    }
    this._unregisterParamate();
  }
}

customElements.define('as-numbox', NumberValueBox);
